import axios from 'axios';
import axiosRetry from 'axios-retry';

export class WindowStickerService {
  static #batchTimeSpan = 150;

  // Disable batching until backend support exists.
  static #maxBatchSize = 1; // global.dsp.settings.oem.api.max_batch_size ?? 3;

  static #instance = null;
  static #batchTimer = null;

  static #itemQueue = [];
  static #items = new Map();

  static get #client() {
    if (WindowStickerService.#instance) {
      return WindowStickerService.#instance;
    }

    const client = axios.create();
    axiosRetry(client, {
      retryCondition: (error) => {
        if (axiosRetry.isNetworkError(error)) {
          return true;
        }

        // if retry condition is not specified, by default idempotent requests are retried
        const statusCode = error.response?.status ?? 500;

        // Once 503's start coming in, no retries will work.
        return statusCode >= 500 && statusCode !== 503;
      },

      retryDelay: axiosRetry.exponentialDelay,
    });

    return (WindowStickerService.#instance = client);
  }

  static async #doRequest(itemId) {
    let request;

    request = WindowStickerService.#client.get('/api/window-sticker-url', {
      params: { id: itemId },
    });

    const { data } = await request;

    return data;
  }

  static async #makeBatchRequest() {
    const items = WindowStickerService.#itemQueue;
    const itemIds = items.map(({ itemId }) => itemId).join(',');

    // Clear and reset the queue.
    WindowStickerService.#itemQueue = [];
    WindowStickerService.#batchTimer = clearTimeout(WindowStickerService.#batchTimer);

    try {
      items.forEach(async (item) => {
        const result = await WindowStickerService.#doRequest(itemIds, true);

        if (result.window_sticker_url) {
          item.resolve(result);
        } else {
          item.reject(result.error ? error : { error: `No result for ${item.itemId}` });
        }
      });
    } catch (error) {
      items.forEach((item) => {
        item.reject(error);
      });
    }
  }

  static #queueRequest(itemId) {
    const itemPromise = new Promise((resolve, reject) => {
      WindowStickerService.#itemQueue.push({ itemId, resolve, reject });
    });

    // Batch all requests made close to the same time.
    if (!WindowStickerService.#batchTimer) {
      WindowStickerService.#batchTimer = setTimeout(() => {
        WindowStickerService.#makeBatchRequest();
      }, WindowStickerService.#batchTimeSpan);
    }

    // But only batch so many requests at once.
    if (WindowStickerService.#itemQueue.length >= WindowStickerService.#maxBatchSize) {
      WindowStickerService.#makeBatchRequest();
    }

    return itemPromise;
  }

  static async fetch(itemId) {
    // Request the incentives if they're not already loaded/loading.
    if (!WindowStickerService.#items.has(itemId)) {
      WindowStickerService.#items.set(itemId, WindowStickerService.#queueRequest(itemId));
    }

    return WindowStickerService.#items.get(itemId);
  }
}
